import Box from '@mui/material/Box';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import { SvgIconTypeMap } from '@mui/material/SvgIcon';
import { usePage } from 'providers/page';
import React from 'react';
import { UseQueryResult } from 'react-query';
import { i18n } from 'i18n';
import {
  generatePath,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams
} from 'react-router-dom';
import { CustomRouteProps } from 'utils';
import { MakePageProps } from './makePage';

export interface withNavigationConfig<TParams, TQueries extends UseQueryResult[] = []> {
  Component: React.ComponentType<MakePageProps<TQueries>>,
  defaultRoute: string;
  pageNavigation: {
    title: string;
    iconComponent: OverridableComponent<SvgIconTypeMap<{}, 'svg'>>;
    routeProps: CustomRouteProps;
    Component: React.ComponentType<MakePageProps<TQueries>>;
  }[],
  pageDataHook?: (params: TParams) => TQueries;
}

const withNavigation = <TParams, TQueries extends UseQueryResult[] = []>({
  Component,
  defaultRoute,
  pageNavigation = [],
  pageDataHook,
}: withNavigationConfig<TParams, TQueries>) => () => {
  const location = useLocation();
  const { actions } = usePage();
  const { setSubNavigation } = actions;
  const params = useParams<any>();

  const queries = pageDataHook ? pageDataHook(params as TParams) : [] as unknown as TQueries;

  const getRelativePath = (fullPath: string) => {
    try {
      const basePath = generatePath('/projects/:entityId/overview', params as { entityId: string | null });
      if (fullPath.startsWith(basePath)) {
        return fullPath.slice(basePath.length).replace(/^\//, '') || '';
      }
      return fullPath.replace(/^\/projects\/[^/]+\/overview\//, '') || '';
    } catch {
      return fullPath.split('/').pop() || '';
    }
  };

  React.useEffect(() => {
    setSubNavigation(pageNavigation.map(({ title, iconComponent, routeProps }) => {
      const templatePath = routeProps.path ?? '';
      if (!templatePath) {
        return {
          title: i18n._(title),
          iconComponent,
          routeProps: { ...routeProps, path: '' },
        };
      }

      const templatePathWithoutWildcard = templatePath.replace(/\/\*$/, '');

      let fullPath = templatePathWithoutWildcard;
      try {
        fullPath = generatePath(templatePathWithoutWildcard, params);
      } catch (e) {
        fullPath = templatePathWithoutWildcard;
      }

      if (!fullPath.startsWith('/')) {
        fullPath = `/${fullPath}`;
      }

      return {
        title: i18n._(title),
        iconComponent,
        routeProps: { ...routeProps, path: fullPath },
      };
    }));

    return () => {
      setSubNavigation([]);
    };
  }, [setSubNavigation, params, pageNavigation]);

  const generatedDefaultRoute = generatePath(defaultRoute, params);
  const relativeDefaultRoute = getRelativePath(generatedDefaultRoute);

  return (
    <Box>
      <Routes>
        <Route
          path="/"
          element={(
            <Navigate to={relativeDefaultRoute} replace />
          )}
        />
        <Route
          path="/*"
          element={(
            <>
              <Component parentQueries={queries} />
              <Routes location={location}>
                {pageNavigation.map((page) => {
                  const templatePath = page.routeProps.path ?? '';
                  const hasWildcard = templatePath.endsWith('/*');
                  const templatePathWithoutWildcard = templatePath.replace(/\/\*$/, '');
                  const fullPath = generatePath(templatePathWithoutWildcard, params);
                  const relativePath = getRelativePath(fullPath);
                  const finalPath = hasWildcard ? `${relativePath}/*` : relativePath;
                  return (
                    <Route
                      key={page.title}
                      path={finalPath}
                      element={<page.Component parentQueries={queries} />}
                    />
                  );
                })}
              </Routes>
            </>
          )}
        />
        <Route
          path="*"
          element={(
            <Navigate to={relativeDefaultRoute} replace />
          )}
        />
      </Routes>
    </Box>
  );
};

export default withNavigation;
